import * as v2 from '@/core/services/api/v2';
import { FILTROS_VAZIOS } from '@/shared/utils/filtrosDaOficina';

/**
 * A exploração (1.2): integrantes, squads e expedições.
 *
 * Três coisas orientam esta store, e as três vêm do servidor:
 *
 * - **Nenhuma fórmula aqui.** `requisitos`, `motivos` e `tempoEstimadoMin` são
 *   calculados pelo domínio e chegam prontos. A tela acende indicador com o que
 *   veio; ela não decide se a squad pode partir.
 * - **A visão geral é a fonte.** Não há WebSocket na 1.2 (D4): ao abrir a
 *   página e ao voltar para ela, `carregar()` diz o que concluiu enquanto o
 *   jogador estava fora. O contador da tela sai de `terminaEm`, e quando ele
 *   zera o card troca sozinho para "receber" — sem consultar o servidor.
 * - **O erro tem código.** A API da 1.2 responde `{ message, codigo }`, e é o
 *   `codigo` que a tela traduz (`exploration.errors.<codigo>`). Guardar a
 *   mensagem do servidor mostraria português para quem joga em chinês.
 */

/** O código estável do erro, quando houver. É por ele que a tela traduz. */
function codigoDoErro(error) {
  return error?.response?.data?.codigo || null;
}

function mensagemDeErro(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.message ||
    'erro'
  );
}

const state = {
  /** O que `GET /exploration` devolveu por último. */
  visao: null,
  integrantes: [],
  candidatos: [],
  /** Biomas da squad escolhida, com veredito para ela. */
  biomas: [],
  materiais: [],
  estoqueDeItens: [],
  catalogoDeItens: [],
  catalogoDeAeronaves: [],
  /**
   * Os sete biomas do catalogo, sem veredito nenhum.
   *
   * Nao e o mesmo que `biomas`, e a diferenca importa: `biomas` sao os biomas
   * **de uma squad**, com o veredito calculado para ela, e so existem depois de
   * o jogador ter uma squad. Este aqui e a tabela, igual para todo mundo — e o
   * que permite ao Posto dizer "chega ate o Deserto Arido" para quem ainda nao
   * montou squad nenhuma.
   */
  catalogoDeBiomas: [],
  /** As cinco facções, com o símbolo que o CMS subiu. */
  guildas: [],
  /** As receitas da oficina, já avaliadas para este jogador. */
  receitas: [],
  /**
   * Os filtros e a página da Oficina. Moram aqui, e não no componente, pela razão da vitrine do
   * mercado: trocar de aba desmonta a Oficina, e quem filtrou "poções que dá para fazer", foi a
   * Materiais conferir o estoque e voltou, achava as 115 receitas de novo.
   */
  filtrosDaOficina: { ...FILTROS_VAZIOS, pagina: 1 },
  /** O resultado do último resgate — o que o modal mostra. */
  resultado: null,
  carregando: false,
  /** `{ codigo, mensagem }` — o código é o que a tela traduz. */
  erro: null,
};

/**
 * O desenho do encaixe enquanto a peça não tem arte: ele diz o que ela **é**. Nome fora do
 * `pixel-icons.css` renderiza vazio e sem erro nenhum, então os três são do sprite.
 */
const ICONE_DO_ITEM = {
  ferramenta: 'hammer',
  armadura: 'shield-alt',
  acessorio: 'gem',
};

const mutations = {
  SET_VISAO(state, visao) {
    state.visao = visao;
  },
  SET_INTEGRANTES(state, integrantes) {
    state.integrantes = integrantes || [];
  },
  /** Troca um integrante da lista pelo que o servidor devolveu — sem reler os trinta. */
  ATUALIZAR_INTEGRANTE(state, integrante) {
    state.integrantes = state.integrantes.map((i) => (i.id === integrante.id ? integrante : i));
  },
  SET_CANDIDATOS(state, candidatos) {
    state.candidatos = candidatos || [];
  },
  /** Contratar tira o candidato da lista: o id dele morreu no servidor. */
  REMOVER_CANDIDATO(state, candidatoId) {
    state.candidatos = state.candidatos.filter((c) => c.candidatoId !== candidatoId);
  },
  SET_BIOMAS(state, biomas) {
    state.biomas = biomas || [];
  },
  SET_MATERIAIS(state, materiais) {
    state.materiais = materiais || [];
  },
  SET_ESTOQUE(state, itens) {
    state.estoqueDeItens = itens || [];
  },
  SET_CATALOGO_DE_ITENS(state, itens) {
    state.catalogoDeItens = itens || [];
  },
  SET_CATALOGO_DE_AERONAVES(state, aeronaves) {
    state.catalogoDeAeronaves = aeronaves || [];
  },
  SET_CATALOGO_DE_BIOMAS(state, biomas) {
    state.catalogoDeBiomas = biomas || [];
  },
  SET_GUILDAS(state, guildas) {
    state.guildas = guildas || [];
  },
  SET_RECEITAS(state, receitas) {
    state.receitas = receitas || [];
  },
  /**
   * Mexer em qualquer filtro volta à página 1 — a página 4 de "Tudo" não é a página 4 de
   * "Poções". E trocar de categoria solta tipo e guilda, que só existem dentro dela: "Anzol"
   * aplicado a poções daria uma lista vazia sem nenhum filtro visível que a explicasse.
   */
  SET_FILTROS_DA_OFICINA(state, ajuste) {
    const atual = state.filtrosDaOficina;
    const novo = { ...atual, ...ajuste };
    if ('categoria' in ajuste && ajuste.categoria !== atual.categoria) {
      novo.tipo = ajuste.tipo ?? '';
      novo.guilda = ajuste.guilda ?? '';
    }
    if (!('pagina' in ajuste)) novo.pagina = 1;
    state.filtrosDaOficina = novo;
  },
  SET_RESULTADO(state, resultado) {
    state.resultado = resultado;
  },
  SET_CARREGANDO(state, valor) {
    state.carregando = valor;
  },
  SET_ERRO(state, erro) {
    state.erro = erro;
  },
};

/** Toda ação que escreve passa por aqui: carrega, limpa erro, recarrega a visão. */
async function executar({ commit, dispatch }, fn, { recarregar = true } = {}) {
  commit('SET_ERRO', null);
  commit('SET_CARREGANDO', true);
  try {
    const resultado = await fn();
    if (recarregar) await dispatch('carregar');
    return resultado;
  } catch (error) {
    commit('SET_ERRO', { codigo: codigoDoErro(error), mensagem: mensagemDeErro(error) });
    throw error;
  } finally {
    commit('SET_CARREGANDO', false);
  }
}

const actions = {
  /**
   * A visão geral. É a única chamada da tela inicial, e é ela que substitui o
   * socket que a 1.2 não tem: ao voltar para a página, o que concluiu aparece.
   */
  async carregar({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;
    const visao = await v2.exploracao(playerId);
    commit('SET_VISAO', visao);
    return visao;
  },

  /**
   * As facções, com nome, descrição e símbolo.
   *
   * Carregada uma vez por sessão: é catálogo, igual para todo mundo, e o
   * servidor já a cacheia por uma hora. Se falhar, a tela cai no ícone de
   * reserva — a facção é regra do domínio, e existe com ou sem arte.
   */
  async carregarGuildas({ commit, state }) {
    if (state.guildas.length > 0) return state.guildas;
    const guildas = await v2.catalogoDeExploracao('guildas');
    commit('SET_GUILDAS', guildas);
    return guildas;
  },

  /**
   * O catálogo de aeronaves, só ele.
   *
   * A squad precisa da ARTE e das medidas de cada modelo para o jogador
   * escolher olhando, e a aeronave que ele possui (`AeronaveDto`) não carrega
   * imagem — só `modeloId`. Quem tinha o catálogo era o Posto, então quem
   * abrisse Squads sem passar pela loja escolhia entre quadrados vazios.
   *
   * Separado de `carregarLoja` porque a squad não quer o catálogo de itens
   * nem o estoque; e guardado como as guildas: é igual para todo mundo e o
   * servidor já o cacheia por uma hora.
   */
  async carregarCatalogoDeAeronaves({ commit, state }) {
    if (state.catalogoDeAeronaves.length > 0) return state.catalogoDeAeronaves;
    const aeronaves = await v2.catalogoDeExploracao('aeronaves');
    commit('SET_CATALOGO_DE_AERONAVES', aeronaves);
    return aeronaves;
  },

  /**
   * O catalogo de biomas, so ele.
   *
   * O alcance de uma aeronave e um **nivel de bioma** (`max_biome_level`), e
   * "nivel 3" nao diz nada a quem esta escolhendo o que comprar. Com a tabela
   * carregada, a loja troca o numero pelo nome do lugar.
   *
   * Nao usa `SET_BIOMAS`: aquele guarda os biomas ja avaliados para uma squad,
   * e sobrescreve-lo aqui apagaria o veredito da tela de expedicao.
   */
  async carregarCatalogoDeBiomas({ commit, state }) {
    if (state.catalogoDeBiomas.length > 0) return state.catalogoDeBiomas;
    const biomas = await v2.catalogoDeExploracao('biomas');
    commit('SET_CATALOGO_DE_BIOMAS', biomas);
    return biomas;
  },

  async carregarIntegrantes({ commit, rootState }, filtros = {}) {
    const playerId = rootState.player?.id;
    if (!playerId) return [];
    const lista = await v2.integrantes(playerId, filtros);
    commit('SET_INTEGRANTES', lista);
    return lista;
  },

  async rolar({ commit, rootState }, { guilda, quantidade = 3 }) {
    const playerId = rootState.player?.id;
    commit('SET_ERRO', null);
    commit('SET_CARREGANDO', true);
    try {
      const candidatos = await v2.rolarCandidatos(playerId, guilda, quantidade);
      commit('SET_CANDIDATOS', candidatos);
      return candidatos;
    } catch (error) {
      commit('SET_ERRO', { codigo: codigoDoErro(error), mensagem: mensagemDeErro(error) });
      throw error;
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },

  async contratar(contexto, candidatoId) {
    const playerId = contexto.rootState.player?.id;
    const integrante = await executar(contexto, () =>
      v2.contratarIntegrante(playerId, candidatoId),
    );
    contexto.commit('REMOVER_CANDIDATO', candidatoId);
    // O ouro saiu: o cabeçalho mostra o saldo, e ele vive na store do jogador.
    await contexto.dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {});
    return integrante;
  },

  /**
   * Equipar num slot. O slot é o do item; só vai no corpo ao desequipar, para
   * dizer qual esvaziar. A visão geral não muda com isso, e o estoque sim —
   * então recarrega o estoque e não a visão.
   */
  async equipar(contexto, { explorerId, explorerItemId, slot }) {
    const playerId = contexto.rootState.player?.id;
    const atualizado = await executar(
      contexto,
      () => v2.equiparIntegrante(playerId, explorerId, explorerItemId, slot),
      { recarregar: false },
    );
    await contexto.dispatch('carregarEstoque').catch(() => {});
    return atualizado;
  },

  /** Usar um consumível. A unidade só sai se o efeito mudou algo (400 `sem_efeito` se não). */
  async usarItem(contexto, { explorerId, explorerItemId }) {
    const playerId = contexto.rootState.player?.id;
    const atualizado = await executar(
      contexto,
      () => v2.usarItemNoIntegrante(playerId, explorerId, explorerItemId),
      { recarregar: false },
    );
    await contexto.dispatch('carregarEstoque').catch(() => {});
    return atualizado;
  },

  /**
   * Distribuir pontos de nível num stat (§9.1). O preço e o saldo são do
   * servidor; a resposta é o integrante inteiro, e ele entra na lista no lugar
   * do antigo — clicar "+" cinco vezes não pode virar cinco releituras da lista.
   */
  async distribuirPontos(contexto, { explorerId, stat, quantidade = 1 }) {
    const playerId = contexto.rootState.player?.id;
    const atualizado = await executar(
      contexto,
      () => v2.distribuirPontosDoIntegrante(playerId, explorerId, stat, quantidade),
      { recarregar: false },
    );
    contexto.commit('ATUALIZAR_INTEGRANTE', atualizado);
    return atualizado;
  },

  async dispensar(contexto, explorerId) {
    const playerId = contexto.rootState.player?.id;
    return executar(contexto, () => v2.dispensarIntegrante(playerId, explorerId));
  },

  async criarSquad(contexto, nome) {
    const playerId = contexto.rootState.player?.id;
    return executar(contexto, () => v2.criarSquad(playerId, nome));
  },

  async editarSquad(contexto, { squadId, ...dados }) {
    const playerId = contexto.rootState.player?.id;
    return executar(contexto, () => v2.editarSquad(playerId, squadId, dados));
  },

  async definirMembros(contexto, { squadId, explorerIds }) {
    const playerId = contexto.rootState.player?.id;
    return executar(contexto, () => v2.definirMembrosDaSquad(playerId, squadId, explorerIds));
  },

  async apagarSquad(contexto, squadId) {
    const playerId = contexto.rootState.player?.id;
    return executar(contexto, () => v2.apagarSquad(playerId, squadId));
  },

  /** Os biomas para **esta** squad, com o veredito e o tempo estimado. */
  async carregarBiomas({ commit, rootState }, squadId) {
    const playerId = rootState.player?.id;
    if (!playerId || !squadId) return [];
    const biomas = await v2.biomasDaSquad(playerId, squadId);
    commit('SET_BIOMAS', biomas);
    return biomas;
  },

  async partir(contexto, { squadId, biomeId }) {
    const playerId = contexto.rootState.player?.id;
    return executar(contexto, () => v2.agendarExpedicao(playerId, squadId, biomeId));
  },

  /**
   * Resgatar. O resultado fica guardado para o modal.
   *
   * Chamar duas vezes é seguro — o servidor devolve o mesmo resultado, e não um
   * erro —, então o botão não precisa se desabilitar para proteger nada.
   */
  async resgatar(contexto, expeditionId) {
    const playerId = contexto.rootState.player?.id;
    const resultado = await executar(contexto, () =>
      v2.resgatarExpedicao(playerId, expeditionId),
    );
    contexto.commit('SET_RESULTADO', resultado);
    await contexto.dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {});
    await contexto.dispatch('carregarMateriais').catch(() => {});
    // Troféu e achado (0050) vão para o estoque de itens, e não para o armazém:
    // sem isto, o item que o modal acabou de mostrar não aparece no seletor de
    // equipamento até outra ação recarregar a lista.
    if (resultado?.achados?.length) await contexto.dispatch('carregarEstoque').catch(() => {});
    return resultado;
  },

  fecharResultado({ commit }) {
    commit('SET_RESULTADO', null);
  },

  async carregarMateriais({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return [];
    const materiais = await v2.materiaisDoJogador(playerId);
    commit('SET_MATERIAIS', materiais);
    return materiais;
  },

  /**
   * Vender material (09/09/2026).
   *
   * O ouro e o armazém mudam juntos, e as duas coisas moram em stores
   * diferentes: sem recarregar as duas, o HUD segue com o ouro velho até a
   * próxima navegação. A receita vem junto porque a oficina conta o que sobrou
   * na prateleira.
   */
  async venderMaterial(contexto, { materialId, quantidade }) {
    const playerId = contexto.rootState.player?.id;
    const venda = await executar(
      contexto,
      () => v2.venderMaterial(playerId, materialId, quantidade),
      { recarregar: false },
    );
    await Promise.all([
      contexto.dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {}),
      contexto.dispatch('carregarMateriais').catch(() => {}),
      contexto.dispatch('carregarReceitas').catch(() => {}),
    ]);
    return venda;
  },

  async carregarEstoque({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return [];
    const itens = await v2.estoqueDeItensDeIntegrante(playerId);
    commit('SET_ESTOQUE', itens);
    return itens;
  },

  /* ---------------------------------------------------------- a oficina --- */

  /**
   * As receitas vêm avaliadas do servidor — "tem 2, precisa 3", quantas vezes
   * dá. A tela não calcula nada; ela mostra. Por isso recarrega depois de todo
   * craft e de todo resgate: os dois mudam o armazém.
   */
  async carregarReceitas({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return [];
    const receitas = await v2.receitasDaOficina(playerId);
    commit('SET_RECEITAS', receitas);
    return receitas;
  },

  async craftar(contexto, { recipeId, vezes = 1 }) {
    const playerId = contexto.rootState.player?.id;
    const resultado = await executar(contexto, () => v2.craftar(playerId, recipeId, vezes), {
      recarregar: false,
    });
    // O ouro saiu, o armazém encolheu e o estoque cresceu: as três telas que
    // mostram isso leem da store.
    await Promise.all([
      contexto.dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {}),
      contexto.dispatch('carregarReceitas').catch(() => {}),
      contexto.dispatch('carregarMateriais').catch(() => {}),
      contexto.dispatch('carregarEstoque').catch(() => {}),
    ]);
    return resultado;
  },

  /* ------------------------------------------------------------- a loja --- */

  /**
   * A prateleira do Posto de Expedição: aeronaves e equipamento na mesma lista.
   *
   * O `LojaPainel` mostra uma lista só, e é assim que o posto funciona — um
   * balcão. O `kind` de cada item é o que diz para qual rota a compra vai; sem
   * ele seriam duas lojas com o mesmo lojista.
   */
  async carregarLoja({ commit, dispatch }) {
    // Os biomas entram aqui porque a prateleira de aeronaves precisa NOMEAR o
    // alcance de cada modelo. Vao na mesma leva das outras duas: sao tres GETs
    // de catalogo, todos cacheados por uma hora no servidor, e servir a
    // prateleira pela metade seria mostrar aeronave sem a medida que a vende.
    const [aeronaves, itens, biomas] = await Promise.all([
      v2.catalogoDeExploracao('aeronaves'),
      v2.catalogoDeExploracao('itens'),
      v2.catalogoDeExploracao('biomas'),
    ]);
    commit('SET_CATALOGO_DE_AERONAVES', aeronaves);
    commit('SET_CATALOGO_DE_ITENS', itens);
    commit('SET_CATALOGO_DE_BIOMAS', biomas);
    await dispatch('carregarEstoque').catch(() => {});
    return [...aeronaves, ...itens];
  },

  async comprarNaLoja(contexto, { item, quantidade = 1 }) {
    const playerId = contexto.rootState.player?.id;
    const compra = await executar(
      contexto,
      () =>
        item.kind === 'aeronave'
          ? v2.comprarAeronave(playerId, item.id)
          : v2.comprarItemDeIntegrante(playerId, item.id, quantidade),
      // A loja não precisa da visão geral inteira; o saldo e o estoque bastam.
      { recarregar: false },
    );
    await contexto.dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {});
    await contexto.dispatch('carregarEstoque').catch(() => {});
    return compra;
  },
};

const getters = {
  /** O símbolo de uma facção, ou `null` enquanto ninguém subiu a arte. */
  simboloDaGuilda: (state) => (guilda) =>
    state.guildas.find((g) => g.guilda === guilda)?.imagem || null,

  nomeDaGuilda: (state) => (guilda) =>
    state.guildas.find((g) => g.guilda === guilda)?.nome || null,

  fichas: (state) => state.visao?.fichas || { usadas: 0, cap: 0, viraEm: null },
  squads: (state) => state.visao?.squads || [],
  /**
   * Quantas squads cabem e o que impede a próxima — vem pronto do domínio
   * (`limiteDeSquads`). `null` até a visão chegar: sem ela a tela não afirma
   * teto nenhum, em vez de afirmar um errado.
   */
  limiteDeSquads: (state) => state.visao?.limiteDeSquads || null,
  /** `running` e `completed`; resgatadas e vencidas ficam no histórico. */
  expedicoes: (state) => state.visao?.expedicoes || [],
  /** As que já voltaram e esperam o resgate. */
  aResgatar: (state) => (state.visao?.expedicoes || []).filter((e) => e.status === 'completed'),
  aeronaves: (state) => state.visao?.aeronaves || [],
  contagemDeIntegrantes: (state) =>
    state.visao?.integrantes || { total: 0, ativos: 0, feridos: 0, emExpedicao: 0, mortos: 0 },
  temFicha: (state, getters) => getters.fichas.usadas < getters.fichas.cap,
  /** O estoque separado pelo verbo: o que se equipa e o que se usa. */
  equipamentosEmEstoque: (state) => state.estoqueDeItens.filter((i) => i.tipo === 'equipamento'),
  consumiveisEmEstoque: (state) => state.estoqueDeItens.filter((i) => i.tipo === 'consumivel'),
  /** As receitas por nível, para a oficina agrupar por "altura do jogo". */
  receitasPorNivel: (state) => {
    const grupos = new Map();
    for (const r of state.receitas) {
      if (!grupos.has(r.nivel)) grupos.set(r.nivel, []);
      grupos.get(r.nivel).push(r);
    }
    return [...grupos.entries()]
      .sort(([a], [b]) => a - b)
      .map(([nivel, receitas]) => ({ nivel, receitas }));
  },
  /**
   * As três prateleiras do Posto, na forma que o `LojaPainel` consome.
   *
   * ## Por que três, e não um balcão só
   *
   * O balcão único misturava **dois catálogos com ids próprios** — aeronave 2 e item 2 existem
   * ao mesmo tempo — e o painel da loja selecionava por id: clicar numa aeronave acendia junto o
   * item de mesmo número (visto na tela em 09/09/2026). Separar arruma isso pela raiz, e é como
   * o resto do jogo já vende: a vila tem uma barraca por tipo de coisa.
   *
   * ## O que cada linha leva
   *
   * `kind` é a rota da compra (aeronave é instância, item é estoque). `chave` é o que o painel
   * usa para selecionar — com ela ele nunca mais depende de o id ser único entre catálogos.
   * `icone` é o desenho que aparece no encaixe **enquanto a peça não tem arte**: dez dos doze
   * itens do posto ainda não têm, e sem isso o encaixe fica um quadrado vazio.
   *
   * `stackable` só para o item: aeronave é instância, e comprar duas de uma vez seria comprar
   * duas aeronaves iguais sem escolher.
   *
   * ## As medidas da aeronave (09/09/2026)
   *
   * A prateleira mostrava nome, arte e preço — e mais nada. Aeronave não é
   * enfeite: ela decide **quantos integrantes cabem na squad** e **até que bioma
   * a squad chega**, que são exatamente as duas coisas que separam um modelo do
   * seguinte. Comprar era escolher entre sete desenhos de avião pelo preço.
   *
   * O alcance vai como `alcanceNome`, e não como o número: `max_biome_level` é
   * uma coluna, não uma informação de jogo — "nível 3" não diz nada a quem está
   * decidindo, e "Deserto Árido" diz tudo. O número fica de reserva para quando
   * o catálogo de biomas ainda não chegou.
   */
  prateleirasDoPosto: (state) => {
    // `|| []` porque a prateleira pode ser lida antes de o catálogo de biomas
    // chegar — e um getter que estoura por causa de um catálogo que ainda está
    // no ar apaga a loja inteira para esconder um rótulo. Sem o nome, cai no
    // número, que é o que `alcanceNome` faz logo abaixo.
    const nomeDoBioma = (nivel) =>
      (state.catalogoDeBiomas || []).find((b) => b.nivel === nivel)?.nome || '';
    const itens = state.catalogoDeItens.map((i) => ({
      id: i.id,
      chave: `item:${i.id}`,
      kind: 'item',
      tipo: i.tipo,
      name: i.nome,
      description: i.descricao,
      price: i.preco,
      rarity: i.raridade,
      image: i.imagem,
      icone: ICONE_DO_ITEM[i.slot] || 'box',
      requiredLevel: 1,
      stackable: true,
      /*
       * O que o item FAZ e QUEM o veste (09/09/2026).
       *
       * Estes cinco campos vêm do catálogo desde sempre — `catalog-exploration.service.ts` os
       * seleciona um a um — e morriam aqui: a prateleira levava nome, arte e preço, e a loja
       * mostrava um encaixe mudo. O caso que quebrou foi a `guilda`: as cinco ferramentas de 900
       * de ouro são **uma por guilda**, com arte parecida e a mesma frase de bônus, e comprar era
       * adivinhar. O servidor só recusa na hora de equipar, e o ouro já foi.
       *
       * `bonus` vem como objeto só com o que não é zero (`{ guildSkill: 1 }`), `efeito` vem sempre
       * preenchido — zerado no equipamento —, e `guilda` nula significa "qualquer integrante".
       * Nenhum deles é conta: é repasse. Quem transforma em frase é `paraQuem.js`, para a loja e a
       * oficina dizerem a mesma coisa.
       */
      slot: i.slot,
      guilda: i.guilda,
      bonus: i.bonus,
      defesa: i.defesa,
      efeito: i.efeito,
    }));

    return {
      aeronaves: state.catalogoDeAeronaves.map((a) => ({
        id: a.id,
        chave: `aeronave:${a.id}`,
        kind: 'aeronave',
        name: a.nome,
        description: a.descricao,
        price: a.preco,
        image: a.imagem,
        icone: 'paper-plane',
        requiredLevel: a.nivelExigidoDoJogador,
        capacidade: a.capacidade,
        alcanceNome: nomeDoBioma(a.alcance) || `Nv. ${a.alcance}`,
        stackable: false,
      })),
      equipamento: itens.filter((i) => i.tipo !== 'consumivel'),
      consumiveis: itens.filter((i) => i.tipo === 'consumivel'),
    };
  },
};

export default { namespaced: true, state, mutations, actions, getters };
